import ApisServiceApp from './ApisServiceApp'
import { normalizeDetailResponse, normalizeListResponse, type ApiListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import { apiRequest, parseListQuery } from '@/utils/apiRequestUtils'
import { blobResponseConfig, ensureExcelBlob } from '@/utils/blobApiUtils'
import { maGocTuHangHoa } from '@/utils/maHangHienThi'
import type {
  DonViTrongLuong,
  HangHoaChiNhanhTonKho,
  HangHoaComboThanhPhan,
  HangHoaDonViQuyDoi,
  HangHoaLoai,
  HangHoaTheKhoQuery,
  HangHoaTheKhoResult,
  HangHoaTonKhoLoc,
  HangHoaTrangThai,
  IProduct,
  LoaiBaoHanhMa,
  PhamViHangHoa,
} from '@/interfaces/productInterfaces'

export const PRODUCT_BASE = '/hang-hoa'

const ALLOWED_SORT_FIELDS = ['maHangHoa', 'tenHangHoa', 'giaBan', 'giaVon', 'tonKho', 'ngayTao'] as const

export interface IHangHoaFlatItem {
  id: string
  maHangHoa: string
  maVach?: string | null
  tenHangHoa: string
  giaBan: number | null
  tonKho: number | null
  trangThai: string
  nhomHangId: string | null
  tenNhomHang?: string
  trangThaiTaiChiNhanh?: string | null
  tonKhoTaiChiNhanh?: number | null
}

export interface IHangHoaThongKe {
  tongSoHangHoa: number
  tongSoMaHang: number
  tongTonKho: number
  tongGiaVon: number
  tongDatNCC: number
}

const toId = (raw: unknown): string | number => {
  if (typeof raw === 'string' || typeof raw === 'number') return raw
  return String(raw ?? '')
}

const parseNum = (raw: unknown): number | null => {
  if (raw === '' || raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const parseLoaiHang = (raw: unknown): HangHoaLoai => {
  const s = String(raw ?? 'hangHoa')
  if (s === 'combo') return 'combo'
  return 'hangHoa'
}

const parseTrangThai = (raw: unknown): HangHoaTrangThai => {
  if (raw === 'ngungKinhDoanh') return 'ngungKinhDoanh'
  if (raw === false || raw === 'false' || raw === 'inactive') return 'ngungKinhDoanh'
  return 'dangKinhDoanh'
}

const parseLoaiBaoHanh = (raw: unknown): LoaiBaoHanhMa | null => {
  const s = String(raw ?? '').trim()
  if (s === 'baoHanh' || s === 'baoTri' || s === 'caHai') return s
  return null
}

const parseDonViTrongLuong = (raw: unknown): DonViTrongLuong | null => {
  const s = String(raw ?? '').trim()
  if (s === 'g' || s === 'kg' || s === 'mg') return s
  return null
}

const parsePhamVi = (raw: unknown): PhamViHangHoa | null => {
  const s = String(raw ?? '').trim()
  if (s === 'toanDuAn' || s === 'theoChiNhanh') return s
  return null
}

const parseIdList = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return []
  return raw.map((x) => Number(x)).filter((n) => Number.isFinite(n))
}

const parseHinhAnhList = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return []
  return raw.map((x) => String(x).trim()).filter(Boolean)
}

const parseComboThanhPhan = (raw: unknown): HangHoaComboThanhPhan[] => {
  if (!Array.isArray(raw)) return []
  return raw
    .map((row) => {
      const o = row as Record<string, unknown>
      const hangHoa = o.hangHoa as Record<string, unknown> | undefined
      const hangHoaId = o.hangHoaId != null ? String(o.hangHoaId).trim() : ''
      const soLuong = Number(o.soLuong)
      const maHangHoa = String(o.maHangHoa ?? hangHoa?.maHangHoa ?? hangHoa?.code ?? '').trim()
      const tenHangHoa = String(o.tenHangHoa ?? hangHoa?.tenHangHoa ?? hangHoa?.name ?? '').trim()
      const giaVon = parseNum(o.giaVon ?? hangHoa?.giaVon)
      const giaBan = parseNum(o.giaBan ?? hangHoa?.giaBan)
      const tonKho = parseNum(o.tonKho ?? hangHoa?.tonKhoTaiChiNhanh ?? hangHoa?.tonKho)
      const quyCachThung = parseNum(o.quyCachThung ?? hangHoa?.quyCachThung ?? hangHoa?.quyCachKien)
      const donViRaw = o.donViTinh ?? hangHoa?.donViTinh ?? hangHoa?.donViCoBan
      const donViTinh =
        typeof donViRaw === 'object' && donViRaw != null
          ? String(
              (donViRaw as Record<string, unknown>).tenDonViTinh ??
                (donViRaw as Record<string, unknown>).ten ??
                '',
            ).trim()
          : String(donViRaw ?? '').trim()
      const maDonViTinh = String(
        o.maDonViTinh ??
          hangHoa?.maDonViTinh ??
          (typeof hangHoa?.donViTinh === 'object' && hangHoa.donViTinh != null
            ? (hangHoa.donViTinh as Record<string, unknown>).maDonViTinh
            : '') ??
          '',
      ).trim()
      if (!hangHoaId || !Number.isFinite(soLuong) || soLuong < 1) return null
      const parsed: HangHoaComboThanhPhan = { hangHoaId, soLuong }
      if (maHangHoa) parsed.maHangHoa = maHangHoa
      const maGoc = maGocTuHangHoa(hangHoa)
      if (maGoc) parsed.hangHoaGocMa = maGoc
      if (tenHangHoa) parsed.tenHangHoa = tenHangHoa
      if (giaVon != null) parsed.giaVon = giaVon
      if (giaBan != null) parsed.giaBan = giaBan
      if (tonKho != null) parsed.tonKho = tonKho
      if (quyCachThung != null) parsed.quyCachThung = quyCachThung
      if (donViTinh) parsed.donViTinh = donViTinh
      if (maDonViTinh) parsed.maDonViTinh = maDonViTinh
      return parsed
    })
    .filter((x): x is HangHoaComboThanhPhan => x != null)
}

export const splitCommaText = (value: unknown): string[] =>
  String(value ?? '')
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)

export const splitCommaNumbers = (value: unknown): number[] =>
  splitCommaText(value)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n))

export const parseDanhSachThanhPhanText = (value: unknown): HangHoaComboThanhPhan[] | undefined => {
  const s = String(value ?? '').trim()
  if (!s) return undefined
  try {
    const parsed = JSON.parse(s) as unknown
    if (!Array.isArray(parsed)) return undefined
    const rows = parseComboThanhPhan(parsed)
    return rows.length ? rows : undefined
  } catch {
    return undefined
  }
}

export const formatDanhSachThanhPhanText = (rows?: HangHoaComboThanhPhan[] | null): string => {
  if (!rows?.length) return ''
  return JSON.stringify(rows.map((r) => ({ hangHoaId: r.hangHoaId, soLuong: r.soLuong })), null, 2)
}

const trongLuongThungKg = (value: number | null, unit: DonViTrongLuong | null): number | null => {
  if (value == null) return null
  if (unit === 'g') return value / 1000
  if (unit === 'mg') return value / 1_000_000
  return value
}

/** numerator / quyCachThung * tonKho. Quy cách 0 hoặc trống thì không tính. */
const tonTheoQuyCach = (
  numerator: number | null,
  quyCach: number | null,
  ton: number | null,
): number | null => {
  if (numerator == null || quyCach == null || ton == null) return null
  if (!(quyCach > 0)) return null
  return (numerator / quyCach) * ton
}

export const mapProductToUi = (raw: Record<string, unknown>): IProduct => {
  const nhom = raw.nhomHang as Record<string, unknown> | undefined
  const nhomIdRaw = raw.nhomHangId ?? nhom?.id
  const trangThaiTaiChiNhanh =
    raw.trangThaiTaiChiNhanh != null ? parseTrangThai(raw.trangThaiTaiChiNhanh) : null
  // Ngừng/mở kinh doanh áp dụng theo chi nhánh đang đăng nhập → hiển thị theo chi nhánh.
  const trangThai =
    trangThaiTaiChiNhanh ?? parseTrangThai(raw.trangThai ?? raw.trangThaiHangHoa)
  const comboRaw = raw.danhSachThanhPhan ?? raw.comboThanhPhan
  const viTriIds = Array.isArray(raw.viTriIds)
    ? raw.viTriIds.map((id) => String(id)).filter(Boolean)
    : []
  const giaVon = parseNum(raw.giaVon)
  const tonKho = parseNum(raw.tonKhoTaiChiNhanh ?? raw.tonKho)
  const trongLuong = parseNum(raw.trongLuong)
  const donViTrongLuong = parseDonViTrongLuong(raw.donViTrongLuong)
  const quyCachTuChu = parseNum(raw.quyCachKien)
  const quyCachThung = parseNum(raw.quyCachThung) ?? quyCachTuChu
  const theTichThung = parseNum(raw.theTichThung)
  return {
    id: toId(raw.id),
    code: String(raw.maHangHoa ?? raw.code ?? ''),
    maVach: raw.maVach != null && String(raw.maVach).trim() !== '' ? String(raw.maVach).trim() : null,
    maQR: raw.maQR != null && String(raw.maQR).trim() !== '' ? String(raw.maQR).trim() : null,
    tenKhaiBaoVi:
      raw.tenKhaiBaoVi != null && String(raw.tenKhaiBaoVi).trim() !== ''
        ? String(raw.tenKhaiBaoVi).trim()
        : null,
    tenKhaiBaoEn:
      raw.tenKhaiBaoEn != null && String(raw.tenKhaiBaoEn).trim() !== ''
        ? String(raw.tenKhaiBaoEn).trim()
        : null,
    name: String(raw.tenHangHoa ?? raw.name ?? ''),
    nhomHangId: nhomIdRaw === '' || nhomIdRaw == null ? null : toId(nhomIdRaw),
    maNhomHang: String(nhom?.maNhom ?? nhom?.ma ?? nhom?.code ?? '').trim(),
    nhomHangLabel: String(nhom?.tenNhom ?? nhom?.ten ?? nhom?.name ?? '').trim(),
    loaiHang: parseLoaiHang(raw.loaiHang),
    trangThai,
    trangThaiTaiChiNhanh,
    isActive: trangThai === 'dangKinhDoanh',
    giaBan: parseNum(raw.giaBan),
    giaVon,
    tonKho,
    giaTriTonKho:
      parseNum(raw.giaTriTonKho) ??
      (giaVon != null && tonKho != null ? giaVon * tonKho : null),
    anhDaiDien:
      raw.anhDaiDien != null && String(raw.anhDaiDien).trim()
        ? String(raw.anhDaiDien).trim()
        : parseHinhAnhList(raw.hinhAnh)[0] ?? null,
    soLuongKhachDat: parseNum(raw.soLuongKhachDat ?? raw.khachDat),
    soLuongDatNcc: parseNum(raw.soLuongDatNcc ?? raw.datNcc),
    datNcc: parseNum(raw.datNcc ?? raw.soLuongDatNcc),
    canhBaoTopSale: Boolean(raw.canhBaoTopSale),
    canhBaoNonmoving: Boolean(raw.canhBaoNonmoving),
    ngayTon: parseNum(raw.ngayTon),
    canhBaoTonCao: Boolean(raw.canhBaoTonCao),
    yeuThich: Boolean(raw.yeuThich),
    banTrucTiep: Boolean(raw.banTrucTiep),
    baoHanh: Boolean(raw.baoHanh),
    quanLySerialIMEI: Boolean(raw.quanLySerialIMEI),
    quanLyTheoHanSuDung: Boolean(raw.quanLyTheoHanSuDung),
    moTa: raw.moTa != null ? String(raw.moTa) : undefined,
    thuongHieuId: raw.thuongHieuId != null && raw.thuongHieuId !== '' ? String(raw.thuongHieuId) : null,
    nhaCungCapId: raw.nhaCungCapId != null && raw.nhaCungCapId !== '' ? String(raw.nhaCungCapId) : null,
    viTriId: viTriIds[0] ?? (raw.viTriId != null && raw.viTriId !== '' ? String(raw.viTriId) : null),
    viTriIds,
    viTriSanPham:
      raw.viTriLabel != null && String(raw.viTriLabel).trim() !== ''
        ? String(raw.viTriLabel).trim()
        : raw.viTriSanPham != null && String(raw.viTriSanPham).trim() !== ''
          ? String(raw.viTriSanPham).trim()
          : null,
    dinhMucTonThapNhat: parseNum(raw.dinhMucTonThapNhat),
    dinhMucTonCaoNhat: parseNum(raw.dinhMucTonCaoNhat),
    trongLuong,
    tongTrongLuongTon:
      parseNum(raw.tongTrongLuongTon) ??
      tonTheoQuyCach(trongLuongThungKg(trongLuong, donViTrongLuong), quyCachThung, tonKho),
    chieuDai: parseNum(raw.chieuDai),
    chieuRong: parseNum(raw.chieuRong),
    chieuCao: parseNum(raw.chieuCao),
    canNangKien: parseNum(raw.canNangKien),
    quyCachKien:
      raw.quyCachKien != null && String(raw.quyCachKien).trim() !== ''
        ? String(raw.quyCachKien).trim()
        : null,
    quyCachThung,
    theTichThung,
    tongTheTichTon:
      parseNum(raw.tongTheTichTon) ?? tonTheoQuyCach(theTichThung, quyCachThung, tonKho),
    donViTrongLuong,
    donViTinhId:
      raw.donViTinhId != null && raw.donViTinhId !== ''
        ? String(raw.donViTinhId)
        : null,
    donViCoBan:
      (raw.donViTinh as { tenDonViTinh?: string } | null | undefined)?.tenDonViTinh ??
      String(raw.donViCoBan ?? ''),
    maDonViTinh:
      (raw.donViTinh as { maDonViTinh?: string } | null | undefined)?.maDonViTinh,
    thoiGianBaoHanh: parseNum(raw.thoiGianBaoHanh),
    loaiBaoHanh: parseLoaiBaoHanh(raw.loaiBaoHanh),
    phamVi: parsePhamVi(raw.phamVi),
    danhSachChiNhanhIds: parseIdList(raw.danhSachChiNhanhIds),
    hinhAnh: parseHinhAnhList(raw.hinhAnh),
    danhSachThanhPhan: parseComboThanhPhan(comboRaw),
    thuocTinhs: Array.isArray(raw.thuocTinhs)
      ? (raw.thuocTinhs as Array<Record<string, unknown>>).map((t) => ({
          ...(typeof t.id === 'string' ? { id: t.id } : {}),
          thuocTinhId: String(t.thuocTinhId ?? ''),
          giaTriId:
            typeof t.giaTriId === 'string' && t.giaTriId ? t.giaTriId : null,
          giaTriTuDo:
            typeof t.giaTriTuDo === 'string' && t.giaTriTuDo ? t.giaTriTuDo : null,
          thuTu: typeof t.thuTu === 'number' ? t.thuTu : undefined,
          thuocTinh:
            t.thuocTinh != null
              ? (t.thuocTinh as { id: string; ten: string; loaiInput: string })
              : null,
          giaTri:
            t.giaTri != null
              ? (t.giaTri as { id: string; giaTri: string })
              : null,
        }))
      : [],
    donViQuyDois: Array.isArray(raw.donViQuyDois)
      ? (raw.donViQuyDois as {
          id?: string
          donViTinhId?: string | null
          tenDonVi?: string | null
          maDonViTinh?: string | null
          tenGoi?: string | null
          tyLeQuyDoi: string | number
          giaBan?: string | number | null
          banTrucTiep?: boolean
          hangHoaPhuId?: string | null
          hangHoaPhu?: { id?: string; maHangHoa?: string; tenHangHoa?: string } | null
          donViTinh?: { maDonViTinh?: string | null; tenDonViTinh?: string | null } | null
        }[]).map((d) => {
          // tenGoi rỗng ("") không được ??-fallback — phải ưu tiên chuỗi có nội dung.
          const tenDonVi =
            [d.tenGoi, d.tenDonVi, d.donViTinh?.tenDonViTinh]
              .map((v) => (v == null ? '' : String(v).trim()))
              .find((v) => v !== '') ?? ''
          const phu = d.hangHoaPhu
          const phuId = String(phu?.id ?? d.hangHoaPhuId ?? '').trim()
          const tyLeRaw = Number((d as { tyLeQuyDoi?: unknown }).tyLeQuyDoi)
          const slCbRaw = Number((d as { soLuongCoBan?: unknown }).soLuongCoBan)
          const slQdRaw = Number((d as { soLuongQuyDoi?: unknown }).soLuongQuyDoi)
          const hasSl =
            Number.isFinite(slCbRaw) &&
            slCbRaw > 0 &&
            Number.isFinite(slQdRaw) &&
            slQdRaw > 0
          const tyLe = Number.isFinite(tyLeRaw) && tyLeRaw > 0 ? tyLeRaw : 1
          return {
            id: d.id,
            donViTinhId: String(d.donViTinhId ?? ''),
            tenDonVi,
            maDonViTinh: d.maDonViTinh ?? d.donViTinh?.maDonViTinh ?? undefined,
            tenGoi: d.tenGoi ?? '',
            soLuongCoBan: hasSl ? slCbRaw : Math.max(1, Math.round(tyLe) || 1),
            soLuongQuyDoi: hasSl ? slQdRaw : 1,
            tyLeQuyDoi: hasSl ? slCbRaw / slQdRaw : tyLe,
            giaBan: d.giaBan == null ? '' : Number(d.giaBan),
            banTrucTiep: Boolean(d.banTrucTiep ?? true),
            hangHoaPhuId: phuId || null,
            hangHoaPhu: phuId
              ? {
                  id: phuId,
                  maHangHoa: String(phu?.maHangHoa ?? '').trim(),
                  tenHangHoa: String(phu?.tenHangHoa ?? '').trim(),
                }
              : null,
          }
        })
      : [],
    note: raw.ghiChu != null ? String(raw.ghiChu) : raw.note != null ? String(raw.note) : undefined,
    createdAt: raw.ngayTao != null ? String(raw.ngayTao) : undefined,
    updatedAt: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : raw.updatedAt != null ? String(raw.updatedAt) : '',
    /**
     * Expand-row tab Tồn kho — list theo chi nhánh. Chỉ BE `chiTiet` mới trả
     * (`hang-hoa.service.chiTiet`); list endpoint `danh-sach-hang-hoa` bỏ qua
     * → trường này sẽ `undefined` ở grid, an toàn cho code phía dưới.
     */
    danhSachChiNhanh: Array.isArray(raw.danhSachChiNhanh)
      ? (() => {
          const byCn = new Map<number, HangHoaChiNhanhTonKho>()
          for (const j of raw.danhSachChiNhanh as Array<Record<string, unknown>>) {
            const chiNhanhId = Number(j.chiNhanhId ?? 0)
            if (!Number.isFinite(chiNhanhId) || chiNhanhId <= 0) continue
            const row: HangHoaChiNhanhTonKho = {
              id: String(j.id ?? ''),
              chiNhanhId,
              ma: String(j.ma ?? ''),
              ten: String(j.ten ?? ''),
              tonKho: parseNum(j.tonKho) ?? 0,
              trangThai: String(j.trangThai ?? 'dangKinhDoanh'),
              datNcc: parseNum(j.datNcc),
              khachDat: parseNum(j.khachDat),
              duKienHetHang: parseNum(j.duKienHetHang),
              canhBaoTopSale: Boolean(j.canhBaoTopSale),
              canhBaoNonmoving: Boolean(j.canhBaoNonmoving),
              ngayTon: parseNum(j.ngayTon),
              canhBaoTonCao: Boolean(j.canhBaoTonCao),
              giaTriTonKho: parseNum(j.giaTriTonKho),
              quyCachThung: parseNum(j.quyCachThung),
              theTichThung: parseNum(j.theTichThung),
              trongLuong: parseNum(j.trongLuong),
              tongTrongLuongTon: parseNum(j.tongTrongLuongTon),
              tongTheTichTon: parseNum(j.tongTheTichTon),
              viTriSanPham:
                j.viTriSanPham != null && String(j.viTriSanPham).trim() !== ''
                  ? String(j.viTriSanPham).trim()
                  : null,
            }
            const prev = byCn.get(chiNhanhId)
            if (!prev || Number(row.tonKho) > Number(prev.tonKho)) {
              byCn.set(chiNhanhId, row)
            }
          }
          return [...byCn.values()]
        })()
      : undefined,
    hangCungLoai: Array.isArray(raw.hangCungLoai)
      ? (raw.hangCungLoai as Array<Record<string, unknown>>).map((h) => ({
          id: String(h.id ?? ''),
          maHangHoa: String(h.maHangHoa ?? ''),
          tenHangHoa: String(h.tenHangHoa ?? ''),
          donViTinhId:
            h.donViTinhId != null && h.donViTinhId !== ''
              ? String(h.donViTinhId)
              : null,
          donViCoBan: String(h.donViCoBan ?? ''),
          maDonViTinh:
            typeof h.maDonViTinh === 'string' ? h.maDonViTinh : undefined,
          giaBan: parseNum(h.giaBan) ?? 0,
          giaVon: parseNum(h.giaVon) ?? 0,
          tyLeQuyDoi: Number(h.tyLeQuyDoi ?? 1),
          tonKho: parseNum(h.tonKho) ?? 0,
          trangThai: String(h.trangThai ?? 'dangKinhDoanh'),
        }))
      : undefined,
    laHangCungLoai: Boolean(raw.laHangCungLoai),
    hangHoaGocId:
      raw.hangHoaGocId != null && raw.hangHoaGocId !== ''
        ? String(raw.hangHoaGocId)
        : null,
    hangHoaGocMa: raw.hangHoaGocMa != null ? String(raw.hangHoaGocMa) : null,
    hangHoaGocTen: raw.hangHoaGocTen != null ? String(raw.hangHoaGocTen) : null,
  }
}

const optionalId = (value: unknown): string | null => {
  if (value === '' || value == null) return null
  return String(value)
}

export const mapUiToProductBody = (payload: Partial<IProduct> & Record<string, unknown>): Record<string, unknown> => {
  const trangThai: HangHoaTrangThai =
    payload.trangThai ??
    (payload.isActive === false ? 'ngungKinhDoanh' : payload.isActive === true ? 'dangKinhDoanh' : 'dangKinhDoanh')

  const parseOptionalNum = (v: unknown): number | null => {
    const s = String(v ?? '').trim()
    if (!s) return null
    const n = Number(s)
    return Number.isFinite(n) ? n : null
  }

  const hinhAnhRaw = payload.hinhAnh
  const hinhAnh = Array.isArray(hinhAnhRaw)
    ? hinhAnhRaw.map((x) => String(x).trim()).filter(Boolean)
    : splitCommaText(hinhAnhRaw)

  const chiNhanhIdsRaw = payload.danhSachChiNhanhIds
  const danhSachChiNhanhIds = Array.isArray(chiNhanhIdsRaw)
    ? chiNhanhIdsRaw.filter((n) => Number.isFinite(Number(n))).map((n) => Number(n))
    : splitCommaNumbers(chiNhanhIdsRaw)

  const comboFromPayload = payload.danhSachThanhPhan
  const comboFromText = parseDanhSachThanhPhanText(payload.danhSachThanhPhanText)
  const danhSachThanhPhan =
    Array.isArray(comboFromPayload) && comboFromPayload.length ? comboFromPayload : comboFromText

  const donViQuyDoisRaw = payload.donViQuyDois
  const donViQuyDois = Array.isArray(donViQuyDoisRaw)
    ? (donViQuyDoisRaw as HangHoaDonViQuyDoi[])
        .map((d) => {
          const slCb = Math.max(1, Math.round(Number(d.soLuongCoBan) || 0))
          const slQd = Math.max(1, Math.round(Number(d.soLuongQuyDoi) || 0))
          const hasSl =
            Number.isFinite(Number(d.soLuongCoBan)) &&
            Number(d.soLuongCoBan) > 0 &&
            Number.isFinite(Number(d.soLuongQuyDoi)) &&
            Number(d.soLuongQuyDoi) > 0
          const tyLeFallback = Number(d.tyLeQuyDoi)
          const tyLeQuyDoi = hasSl
            ? slCb / slQd
            : Number.isFinite(tyLeFallback) && tyLeFallback > 0
              ? tyLeFallback
              : NaN
          return {
            ...(d.id ? { id: d.id } : {}),
            donViTinhId: String(d.donViTinhId ?? '').trim(),
            tenGoi: String(d.tenGoi ?? '').trim() || undefined,
            soLuongCoBan: hasSl ? slCb : Math.max(1, Math.round(tyLeFallback) || 1),
            soLuongQuyDoi: hasSl ? slQd : 1,
            tyLeQuyDoi,
            giaBan: d.giaBan == null || d.giaBan === '' ? 0 : Number(d.giaBan),
            banTrucTiep: d.banTrucTiep ?? true,
          }
        })
        .filter(
          (d) =>
            d.donViTinhId &&
            Number.isFinite(d.tyLeQuyDoi) &&
            d.tyLeQuyDoi > 0,
        )
    : []

  /** Thuộc tính hàng hóa — luôn gửi (kể cả []) để backend create/update đồng bộ.
   *  Lọc dòng hợp lệ: có thuocTinhId + (giaTriId hoặc giaTriTuDo). */
  const thuocTinhsRaw = payload.thuocTinhs
  const thuocTinhs = Array.isArray(thuocTinhsRaw)
    ? (thuocTinhsRaw as Array<Record<string, unknown>>)
        .map((t) => ({
          ...(typeof t.id === 'string' && t.id ? { id: t.id } : {}),
          thuocTinhId: String(t.thuocTinhId ?? '').trim(),
          giaTriId:
            typeof t.giaTriId === 'string' && t.giaTriId.trim()
              ? t.giaTriId.trim()
              : null,
          giaTriTuDo:
            typeof t.giaTriTuDo === 'string' && t.giaTriTuDo.trim()
              ? t.giaTriTuDo.trim()
              : null,
        }))
        .filter(
          (t) =>
            t.thuocTinhId &&
            (t.giaTriId != null || (t.giaTriTuDo != null && t.giaTriTuDo !== '')),
        )
    : []

  const body: Record<string, unknown> = {
    maHangHoa: String(payload.code ?? '').trim(),
    maVach: String(payload.maVach ?? '').trim() || null,
    maQR: String(payload.maQR ?? '').trim() || null,
    tenKhaiBaoVi: String(payload.tenKhaiBaoVi ?? '').trim() || null,
    tenKhaiBaoEn: String(payload.tenKhaiBaoEn ?? '').trim() || null,
    tenHangHoa: String(payload.name ?? '').trim(),
    nhomHangId: optionalId(payload.nhomHangId),
    loaiHang: (payload.loaiHang ?? 'hangHoa') as HangHoaLoai,
    thuongHieuId: optionalId(payload.thuongHieuId),
    nhaCungCapId: optionalId(payload.nhaCungCapId),
    viTriId: optionalId(payload.viTriId),
    viTriIds: Array.isArray(payload.viTriIds)
      ? payload.viTriIds.map((id) => String(id)).filter(Boolean)
      : [],
    moTa: String(payload.moTa ?? '').trim() || null,
    giaBan: payload.giaBan == null ? null : Number(payload.giaBan),
    giaVon: payload.giaVon == null ? null : Number(payload.giaVon),
    ...(payload.tonKho == null ? {} : { tonKho: Number(payload.tonKho) }),
    dinhMucTonThapNhat:
      payload.dinhMucTonThapNhat == null ? null : parseOptionalNum(payload.dinhMucTonThapNhat),
    dinhMucTonCaoNhat:
      payload.dinhMucTonCaoNhat == null ? null : parseOptionalNum(payload.dinhMucTonCaoNhat),
    trongLuong: payload.trongLuong == null ? null : parseOptionalNum(payload.trongLuong),
    chieuDai: payload.chieuDai == null ? null : parseOptionalNum(payload.chieuDai),
    chieuRong: payload.chieuRong == null ? null : parseOptionalNum(payload.chieuRong),
    chieuCao: payload.chieuCao == null ? null : parseOptionalNum(payload.chieuCao),
    canNangKien: payload.canNangKien == null ? null : parseOptionalNum(payload.canNangKien),
    quyCachKien: String(payload.quyCachKien ?? '').trim() || null,
    quyCachThung: payload.quyCachThung == null ? null : parseOptionalNum(payload.quyCachThung),
    theTichThung: payload.theTichThung == null ? null : parseOptionalNum(payload.theTichThung),
    donViTrongLuong: payload.donViTrongLuong ?? null,
    donViTinhId:
      payload.donViTinhId != null && payload.donViTinhId !== ''
        ? String(payload.donViTinhId)
        : null,
    banTrucTiep: payload.banTrucTiep ?? false,
    baoHanh: payload.baoHanh ?? false,
    quanLySerialIMEI: payload.quanLySerialIMEI ?? false,
    quanLyTheoHanSuDung: payload.quanLyTheoHanSuDung ?? false,
    thoiGianBaoHanh:
      payload.thoiGianBaoHanh == null ? null : parseOptionalNum(payload.thoiGianBaoHanh),
    loaiBaoHanh: payload.loaiBaoHanh ?? null,
    phamVi: payload.phamVi ?? null,
    ghiChu: String(payload.note ?? '').trim() || null,
    trangThai,
    ...(hinhAnh.length ? { hinhAnh } : {}),
    ...(danhSachChiNhanhIds.length ? { danhSachChiNhanhIds } : {}),
    ...(danhSachThanhPhan?.length ? { danhSachThanhPhan } : {}),
    ...(donViQuyDois.length ? { donViQuyDois } : {}),
    /** Luôn gửi thuocTinhs (kể cả []) để cho phép xóa hết thuộc tính khi update. */
    thuocTinhs,
  }
  Object.keys(body).forEach((k) => {
    if (body[k] === '') body[k] = null
  })
  return body
}

export const buildProductListParams = (query: Record<string, unknown>): Record<string, string | number | boolean | undefined> => {
  const pageRaw = Number(query.page)
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1
  const psRaw = Number(query.pageSize)
  const pageSize = Number.isFinite(psRaw) && psRaw > 0 ? psRaw : 15

  const search = String(query.searchQuery ?? query.search ?? '').trim() || undefined

  const maHangHoa = String(query.maHangHoa ?? '').trim() || undefined
  const tenHangHoa = String(query.tenHangHoa ?? '').trim() || undefined
  const maNhomHang = String(query.maNhomHang ?? '').trim() || undefined
  const tenNhomHang = String(query.tenNhomHang ?? '').trim() || undefined
  const maVach = String(query.maVach ?? '').trim() || undefined

  const ngayTaoTuRaw = String(query.ngayTaoTu ?? '').trim()
  const ngayTaoDenRaw = String(query.ngayTaoDen ?? '').trim()
  const ngayTaoTu = /^\d{4}-\d{2}-\d{2}$/.test(ngayTaoTuRaw) ? ngayTaoTuRaw : undefined
  const ngayTaoDen = /^\d{4}-\d{2}-\d{2}$/.test(ngayTaoDenRaw) ? ngayTaoDenRaw : undefined

  const nhomHangIdRaw = query.nhomHangId
  const nhomHangIds = (Array.isArray(nhomHangIdRaw) ? nhomHangIdRaw : [nhomHangIdRaw])
    .flatMap((v) => (v == null ? [] : String(v).split(',')))
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
  const nhomHangIdsUnique = Array.from(new Set(nhomHangIds))
  const nhomHangId = nhomHangIdsUnique.length > 0 ? nhomHangIdsUnique.join(',') : undefined

  let loaiHang: HangHoaLoai | undefined
  const loaiRaw = String(query.loaiHang ?? '').trim()
  if (loaiRaw === 'hangHoa' || loaiRaw === 'combo') {
    loaiHang = loaiRaw
  }

  let trangThai: HangHoaTrangThai | undefined
  if (query.isActive === 'all') trangThai = undefined
  else if (query.isActive === 'true' || query.isActive === true) trangThai = 'dangKinhDoanh'
  else if (query.isActive === 'false' || query.isActive === false) trangThai = 'ngungKinhDoanh'

  let tonKho: HangHoaTonKhoLoc | undefined
  const tonRaw = String(query.tonKho ?? '').trim()
  if (tonRaw === 'conHang' || tonRaw === 'hetHang' || tonRaw === 'tatCa') tonKho = tonRaw

  let yeuThich: boolean | undefined
  if (query.yeuThich === 'true' || query.yeuThich === true) yeuThich = true
  else if (query.yeuThich === 'false' || query.yeuThich === false) yeuThich = false

  let banTrucTiep: boolean | undefined
  if (query.banTrucTiep === 'true' || query.banTrucTiep === true) banTrucTiep = true
  else if (query.banTrucTiep === 'false' || query.banTrucTiep === false) banTrucTiep = false

  let baoHanh: boolean | undefined
  if (query.baoHanh === 'true' || query.baoHanh === true) baoHanh = true
  else if (query.baoHanh === 'false' || query.baoHanh === false) baoHanh = false

  let quanLyTheoHanSuDung: boolean | undefined
  if (query.quanLyTheoHanSuDung === 'true' || query.quanLyTheoHanSuDung === true)
    quanLyTheoHanSuDung = true
  else if (
    query.quanLyTheoHanSuDung === 'false' ||
    query.quanLyTheoHanSuDung === false
  )
    quanLyTheoHanSuDung = false

  const sortFieldRaw = String(query.sortField ?? '').trim()
  const sortOrderRaw = String(query.sortOrder ?? query.sortDirection ?? '').trim().toLowerCase()
  const sortField = ALLOWED_SORT_FIELDS.includes(sortFieldRaw as (typeof ALLOWED_SORT_FIELDS)[number])
    ? sortFieldRaw
    : undefined
  const sortOrder =
    sortField && (sortOrderRaw === 'asc' || sortOrderRaw === 'desc') ? sortOrderRaw : undefined

  // Gộp hàng cùng loại: mặc định true (chỉ trả hàng gốc + nested hangCungLoai).
  // Cho phép override=false (vd. POS search) để các hàng thành phần trả thành dòng riêng,
  // search được theo mã riêng (HH0010-1).
  let gomHangCungLoai = true
  const gomRaw = query.gomHangCungLoai
  if (gomRaw === 'false' || gomRaw === false) gomHangCungLoai = false
  else if (gomRaw === 'true' || gomRaw === true) gomHangCungLoai = true

  return {
    page,
    pageSize,
    // Gộp hàng cùng loại: chỉ trả hàng gốc + kèm danh sách variant để FE render combobox đơn vị
    gomHangCungLoai,
    ...(search ? { search } : {}),
    ...(maHangHoa ? { maHangHoa } : {}),
    ...(tenHangHoa ? { tenHangHoa } : {}),
    ...(maNhomHang ? { maNhomHang } : {}),
    ...(tenNhomHang ? { tenNhomHang } : {}),
    ...(maVach ? { maVach } : {}),
    ...(ngayTaoTu ? { ngayTaoTu } : {}),
    ...(ngayTaoDen ? { ngayTaoDen } : {}),
    ...(nhomHangId ? { nhomHangId } : {}),
    ...(loaiHang ? { loaiHang } : {}),
    ...(trangThai ? { trangThai } : {}),
    ...(tonKho ? { tonKho } : {}),
    ...(yeuThich !== undefined ? { yeuThich } : {}),
    ...(banTrucTiep !== undefined ? { banTrucTiep } : {}),
    ...(baoHanh !== undefined ? { baoHanh } : {}),
    ...(quanLyTheoHanSuDung !== undefined ? { quanLyTheoHanSuDung } : {}),
    ...(sortField && sortOrder ? { sortField, sortOrder } : {}),
  }
}

const mapHangHoaFlat = (raw: Record<string, unknown>): IHangHoaFlatItem => {
  const nhom = raw.nhomHang as Record<string, unknown> | undefined
  return {
    id: toId(raw.id) as string,
    maHangHoa: String(raw.maHangHoa ?? ''),
    maVach: raw.maVach != null && String(raw.maVach).trim() !== '' ? String(raw.maVach) : null,
    tenHangHoa: String(raw.tenHangHoa ?? ''),
    giaBan: parseNum(raw.giaBan),
    tonKho: parseNum(raw.tonKho),
    trangThai: String(raw.trangThai ?? ''),
    nhomHangId: raw.nhomHangId != null ? String(raw.nhomHangId) : null,
    tenNhomHang: nhom?.ten != null ? String(nhom.ten) : undefined,
    trangThaiTaiChiNhanh: raw.trangThaiTaiChiNhanh != null ? String(raw.trangThaiTaiChiNhanh) : null,
    tonKhoTaiChiNhanh: parseNum(raw.tonKhoTaiChiNhanh),
  }
}

export async function fetchProductFlatList(params?: { trangThai?: string }): Promise<IHangHoaFlatItem[]> {
  const res = (await apiRequest(
    ApisServiceApp.get(`${PRODUCT_BASE}/danh-sach-hang-hoa-flat`, { params }),
  )) as unknown
  const obj = res && typeof res === 'object' ? (res as Record<string, unknown>) : {}
  const rows = (obj.results ?? obj.data ?? res) as unknown
  if (!Array.isArray(rows)) return []
  return rows.map((r) => mapHangHoaFlat(r as Record<string, unknown>))
}

export async function fetchProductStats(): Promise<IHangHoaThongKe> {
  const res = (await apiRequest(ApisServiceApp.get(`${PRODUCT_BASE}/thong-ke-hang-hoa`))) as unknown
  const raw = (res && typeof res === 'object' ? (res as Record<string, unknown>) : {}) as Record<string, unknown>
  const data = (raw.data && typeof raw.data === 'object' ? raw.data : raw) as Record<string, unknown>
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0)
  return {
    tongSoHangHoa: num(data.tongSoHangHoa),
    tongSoMaHang: num(data.tongSoMaHang ?? data.tongSoHangHoa),
    tongTonKho: num(data.tongTonKho),
    tongGiaVon: num(data.tongGiaVon),
    tongDatNCC: num(data.tongDatNCC),
  }
}

export async function getListProduct(querySt?: string): Promise<PagedListResult<IProduct>> {
  const q = parseListQuery(querySt) as Record<string, unknown>
  const params = buildProductListParams(q)
  const res = (await apiRequest(
    ApisServiceApp.get(`${PRODUCT_BASE}/danh-sach-hang-hoa`, { params }),
  )) as ApiListResponse<Record<string, unknown>> | Record<string, unknown>[]
  const normalized = normalizeListResponse<Record<string, unknown>>(res)
  return { data: normalized.data.map((row) => mapProductToUi(row)), paging: normalized.paging }
}

export async function getDetailProduct(id: string | number): Promise<IProduct> {
  const res = await apiRequest(ApisServiceApp.get(`${PRODUCT_BASE}/${id}/chi-tiet-hang-hoa`))
  const raw = normalizeDetailResponse<Record<string, unknown>>(res as Record<string, unknown> | { data?: Record<string, unknown> })
  return mapProductToUi(raw)
}

const buildTheKhoParams = (query: Record<string, string | number | boolean | undefined>) => {
  const params: Record<string, string | number | boolean> = {}
  Object.entries(query).forEach(([k, v]) => {
    if (v == null) return
    const s = typeof v === 'string' ? v.trim() : v
    if (s === '') return
    params[k] = s
  })
  return params
}

export async function fetchTheKhoHangHoaExcelBlob(
  id: string | number,
  query: Omit<HangHoaTheKhoQuery, 'page' | 'pageSize'> & { hienGiaVon?: boolean } = {},
): Promise<Blob> {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PRODUCT_BASE}/${id}/xuat-the-kho-hang-hoa`, {
      ...blobResponseConfig,
      params: buildTheKhoParams(query),
    }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export async function getTheKhoHangHoa(
  id: string | number,
  query: HangHoaTheKhoQuery = {},
): Promise<HangHoaTheKhoResult> {
  const params = buildTheKhoParams(query)
  const res = await apiRequest(ApisServiceApp.get(`${PRODUCT_BASE}/${id}/the-kho-hang-hoa`, { params }))
  // TransformInterceptor: `{ results, paging, ...tong }` → `{ data: results[], paging, ...tong }`
  const envelope = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>
  const { data: list, paging } = normalizeListResponse<Record<string, unknown>>(
    res as ApiListResponse<Record<string, unknown>>,
  )
  const sumNum = (k: string) => parseNum(envelope[k]) ?? 0

  return {
    paging,
    tonHienTai: sumNum('tonHienTai'),
    tonDauKy: sumNum('tonDauKy'),
    tonCuoiKy: sumNum('tonCuoiKy'),
    tonDuKien: envelope.tonDuKien != null ? sumNum('tonDuKien') : sumNum('tonHienTai'),
    tongNhap: sumNum('tongNhap'),
    tongXuat: sumNum('tongXuat'),
    results: list.map((r) => ({
      id: String(r.id ?? ''),
      phieuId: String(r.phieuId ?? ''),
      maPhieu: String(r.maPhieu ?? ''),
      loaiPhieu: String(r.loaiPhieu ?? ''),
      huong: String(r.huong ?? ''),
      trangThaiPhieu:
        r.trangThaiPhieu != null && String(r.trangThaiPhieu).trim() !== ''
          ? String(r.trangThaiPhieu)
          : undefined,
      ngayChungTu: String(r.ngayChungTu ?? ''),
      ngayTao: String(r.ngayTao ?? ''),
      soLuong: parseNum(r.soLuong) ?? 0,
      soLuongNhap: parseNum(r.soLuongNhap) ?? 0,
      soLuongXuat: parseNum(r.soLuongXuat) ?? 0,
      tonCuoi: parseNum(r.tonCuoi),
      donGia: parseNum(r.donGia) ?? 0,
      giaVon: parseNum(r.giaVon) ?? 0,
      thanhTien: parseNum(r.thanhTien) ?? 0,
      doiTac: r.doiTac != null && String(r.doiTac).trim() !== '' ? String(r.doiTac) : null,
      maKhachHang:
        r.maKhachHang != null && String(r.maKhachHang).trim() !== ''
          ? String(r.maKhachHang)
          : null,
      tenKhachHang:
        r.tenKhachHang != null && String(r.tenKhachHang).trim() !== ''
          ? String(r.tenKhachHang)
          : null,
      chungTuLienQuanId:
        r.chungTuLienQuanId != null && String(r.chungTuLienQuanId).trim() !== ''
          ? String(r.chungTuLienQuanId)
          : null,
      maChungTuLienQuan:
        r.maChungTuLienQuan != null && String(r.maChungTuLienQuan).trim() !== ''
          ? String(r.maChungTuLienQuan)
          : null,
      loaiChungTuLienQuan: r.loaiChungTuLienQuan != null ? String(r.loaiChungTuLienQuan) : null,
      lyDo: r.lyDo != null && String(r.lyDo).trim() !== '' ? String(r.lyDo) : null,
      ghiChu: r.ghiChu != null && String(r.ghiChu).trim() !== '' ? String(r.ghiChu) : null,
    })),
  }
}

export const createProduct = (data: Partial<IProduct>) =>
  apiRequest(ApisServiceApp.post(`${PRODUCT_BASE}/tao-hang-hoa`, mapUiToProductBody(data)))

export const updateProduct = (id: string | number, data: Partial<IProduct>) =>
  apiRequest(ApisServiceApp.put(`${PRODUCT_BASE}/${id}/cap-nhat-hang-hoa`, mapUiToProductBody(data)))

export const deleteProduct = (id: string | number) =>
  apiRequest(ApisServiceApp.delete(`${PRODUCT_BASE}/${id}/xoa-hang-hoa`))

/** DEV/TEST: xóa sạch PKCH/KK/lô của SP QC-TON-* / QC-GV-*, reset tồn = 0. */
export const wipeGiaoDichTestHangHoa = (id: string | number) =>
  apiRequest(ApisServiceApp.post(`${PRODUCT_BASE}/${id}/wipe-giao-dich-test`, {}))

export const toggleProductStatus = (
  id: string | number,
  trangThai: 'dangKinhDoanh' | 'ngungKinhDoanh',
) =>
  apiRequest(ApisServiceApp.patch(`${PRODUCT_BASE}/${id}/chuyen-trang-thai-hang-hoa`, { trangThai }))

export const toggleProductFavorite = (id: string | number) =>
  apiRequest(ApisServiceApp.patch(`${PRODUCT_BASE}/${id}/chuyen-trang-thai-yeu-thich`, {}))

export const deleteManyProducts = (ids: Array<string | number>) =>
  apiRequest(
    ApisServiceApp.delete(`${PRODUCT_BASE}/xoa-nhieu-hang-hoa`, {
      data: { danhSachId: ids.map((x) => String(x)) },
    }),
  )

export async function fetchProductExcelBlob(
  listQueryString?: string,
  options?: { columns?: string[] },
): Promise<Blob> {
  const q = parseListQuery(listQueryString ?? '') as Record<string, unknown>
  const body: Record<string, unknown> = {
    ...buildProductListParams(q),
  }
  // Chỉ đính kèm `columns` khi user tùy chọn (giữ tương thích BE cũ khi không truyền).
  if (options?.columns && options.columns.length > 0) {
    body.columns = options.columns
  }
  const blob = (await apiRequest(
    ApisServiceApp.post<Blob>(`${PRODUCT_BASE}/xuat-hang-hoa`, body, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export type ProductRecord = IProduct
export const fetchProductList = (querySt?: string): Promise<PagedListResult<IProduct>> => getListProduct(querySt)

/** Hàng hóa khác có tên trùng chính xác (không phân biệt hoa/thường) — null nếu không có. */
export async function timHangHoaTrungTen(
  ten: string,
  boQuaId?: string | number,
): Promise<IProduct | null> {
  const t = ten.trim().toLowerCase()
  if (!t) return null
  const res = await fetchProductList(
    `tenHangHoa=${encodeURIComponent(ten.trim())}&pageSize=50&gomHangCungLoai=false&isActive=all`,
  )
  return (
    res.data.find(
      (p) => String(p.name ?? '').trim().toLowerCase() === t && String(p.id) !== String(boQuaId ?? ''),
    ) ?? null
  )
}
export const fetchProductDetail = (id: string | number): Promise<IProduct> => getDetailProduct(id)
export const createProductRecord = (data: Partial<IProduct>) => createProduct(data)
export const updateProductRecord = (id: string | number, data: Partial<IProduct>) => updateProduct(id, data)
export const deleteProductRecord = (id: string | number) => deleteProduct(id)

export type HangHoaLoItem = {
  id: string
  hangHoaId: string
  chiNhanhId: number
  maLo: string
  hanSuDung: string | null
  soLuong: number
  giaVon: number | null
  trangThai: string
  ngayNhap: string
  /** Derived: 'khongRo' | 'conHan' | 'canHan' | 'hetHan' — computed by backend. */
  trangThaiHan?: string
}

export type HangHoaDonViTinhIn = {
  id: string
  dvt: string
  maHangCoBan: string
  dvtCoBan: string
  /** 1 đơn vị của hàng này = `tyLeQuyDoi` đơn vị cơ bản (hàng gốc = 1). */
  tyLeQuyDoi: number
  soLuongCoBan?: number
  soLuongQuyDoi?: number
  donViQuyDoi: {
    ten: string
    tyLe: number
    soLuongCoBan?: number
    soLuongQuyDoi?: number
  }[]
}

/** ĐVT cơ bản / quy đổi cho mẫu in — chia lô để URL không quá dài. */
export async function fetchDonViTinhIn(hangHoaIds: string[]): Promise<HangHoaDonViTinhIn[]> {
  const ids = [...new Set(hangHoaIds.map((id) => String(id || '').trim()).filter(Boolean))]
  const out: HangHoaDonViTinhIn[] = []
  for (let i = 0; i < ids.length; i += 150) {
    const qs = new URLSearchParams({ hangHoaIds: ids.slice(i, i + 150).join(',') })
    const res = (await ApisServiceApp.get(`${PRODUCT_BASE}/don-vi-tinh-in?${qs.toString()}`)) as unknown
    const list = Array.isArray(res) ? res : (res as { data?: unknown } | null)?.data
    if (Array.isArray(list)) out.push(...(list as HangHoaDonViTinhIn[]))
  }
  return out
}

async function fetchHangHoaTheoChiNhanhRows(params: {
  hangHoaIds: string[]
  chiNhanhId?: number
}): Promise<Record<string, unknown>[]> {
  const ids = [...new Set(params.hangHoaIds.map((id) => String(id || '').trim()).filter(Boolean))]
  if (!ids.length) return []
  const qs = new URLSearchParams({ hangHoaIds: ids.join(',') })
  if (params.chiNhanhId != null && Number.isFinite(params.chiNhanhId) && params.chiNhanhId > 0) {
    qs.set('chiNhanhId', String(params.chiNhanhId))
  }
  const res = (await apiRequest(
    ApisServiceApp.get(`${PRODUCT_BASE}/ton-kho-theo-chi-nhanh?${qs.toString()}`),
  )) as unknown
  const { data } = normalizeListResponse(
    (res && typeof res === 'object' ? res : { results: [] }) as Parameters<typeof normalizeListResponse>[0],
  )
  return data.filter(
    (raw): raw is Record<string, unknown> =>
      !!raw && typeof raw === 'object' && String((raw as Record<string, unknown>).hangHoaId ?? '') !== '',
  )
}

export async function fetchTonKhoTheoChiNhanh(params: {
  hangHoaIds: string[]
  chiNhanhId?: number
}): Promise<Record<string, number>> {
  const out: Record<string, number> = {}
  for (const row of await fetchHangHoaTheoChiNhanhRows(params)) {
    const n = Number(row.tonKho)
    out[String(row.hangHoaId)] = Number.isFinite(n) ? n : 0
  }
  return out
}

/** Tồn + trạng thái kinh doanh tại chi nhánh (trangThai null = chưa có bản ghi chi nhánh). */
export async function fetchTonKhoVaTrangThaiTheoChiNhanh(params: {
  hangHoaIds: string[]
  chiNhanhId?: number
}): Promise<Record<string, { tonKho: number; trangThai: string | null }>> {
  const out: Record<string, { tonKho: number; trangThai: string | null }> = {}
  for (const row of await fetchHangHoaTheoChiNhanhRows(params)) {
    const n = Number(row.tonKho)
    out[String(row.hangHoaId)] = {
      tonKho: Number.isFinite(n) ? n : 0,
      trangThai: row.trangThai != null ? String(row.trangThai) : null,
    }
  }
  return out
}

export async function fetchHangHoaLo(params: {
  hangHoaId: string
  chiNhanhId?: number
  conHang?: boolean
}): Promise<{ items: HangHoaLoItem[]; quanLyTheoHanSuDung: boolean }> {
  const qs = new URLSearchParams({ hangHoaId: params.hangHoaId })
  if (params.chiNhanhId != null)
    qs.set('chiNhanhId', String(params.chiNhanhId))
  if (params.conHang) qs.set('conHang', 'true')
  const res = await ApisServiceApp.get(
    `${PRODUCT_BASE}/danh-sach-lo?${qs.toString()}`,
  )
  const raw = (res.data?.data ?? res.data) as Record<string, unknown>
  return {
    items: Array.isArray(raw.items)
      ? (raw.items as Record<string, unknown>[]).map((r) => ({
          id: String(r.id ?? ''),
          hangHoaId: String(r.hangHoaId ?? ''),
          chiNhanhId: Number(r.chiNhanhId ?? 0),
          maLo: String(r.maLo ?? ''),
          hanSuDung: r.hanSuDung != null ? String(r.hanSuDung) : null,
          soLuong: Number(r.soLuong ?? 0),
          giaVon: r.giaVon != null ? Number(r.giaVon) : null,
          trangThai: String(r.trangThai ?? 'dangKinhDoanh'),
          ngayNhap: String(r.ngayNhap ?? ''),
          trangThaiHan: r.trangThaiHan != null ? String(r.trangThaiHan) : undefined,
        }))
      : [],
    quanLyTheoHanSuDung: Boolean(raw.quanLyTheoHanSuDung ?? false),
  }
}

export async function taiFileMauNhapHangHoaBlob(): Promise<Blob> {
  const { buildHangHoaImportTemplateBlob } = await import(
    '@/components/admin/product/partials/hangHoaExcelImportParse'
  )
  return buildHangHoaImportTemplateBlob()
}

export type HangHoaDuLieuImportPayload = {
  rows: Array<Record<string, unknown>>
  dvtRows: Array<Record<string, unknown>>
  references?: Record<string, unknown>
}

/** Lấy JSON dữ liệu hàng hóa — FE tự build file Excel. */
export async function layDuLieuHangHoaImport(): Promise<HangHoaDuLieuImportPayload> {
  const raw = (await apiRequest(
    ApisServiceApp.get(`${PRODUCT_BASE}/du-lieu-hang-hoa-import`),
  )) as { data?: HangHoaDuLieuImportPayload } & HangHoaDuLieuImportPayload
  return (raw?.data ?? raw) as HangHoaDuLieuImportPayload
}

export async function taiFileDuLieuHangHoaBlob(): Promise<Blob> {
  const payload = await layDuLieuHangHoaImport()
  const { buildHangHoaImportDataBlob } = await import(
    '@/components/admin/product/partials/hangHoaExcelImportParse'
  )
  return buildHangHoaImportDataBlob(payload as never)
}

// ============================================================================
// FLOW "NHẬP CẬP NHẬT HÀNG HÓA" — FE parse Excel → POST JSON object
// 2 bước: validate dry-run → apply. Có xử lý mã vạch + phạm vi chi nhánh.
// ============================================================================

export type NhapCapNhatHangHoaOptions = {
  xuLyTrungMaKhacTen?: 'baoLoi' | 'thayThe'
  xuLyTrungMaVachKhacMa?: 'baoLoi' | 'thayTheMa'
  phamViApDungGiaVon?: 'toanHeThong' | 'theoChiNhanh'
  danhSachChiNhanhGiaVon?: number[]
  phamViApDungTrangThai?: 'toanHeThong' | 'theoChiNhanh'
  danhSachChiNhanhTrangThai?: number[]
  capNhatTonKho?: boolean
  capNhatMoTa?: boolean
  capNhatGiaVon?: boolean
  capNhatDonViQuyDoi?: boolean
}

export type ChiTietLoiNhapCapNhat = {
  dong: number
  maHangHoa?: string
  loi: string
}

export type KetQuaXacNhanNhapCapNhat = {
  hopLe: boolean
  tongSoDong: number
  soDongHopLe: number
  soDongLoi: number
  soDongCapNhat: number
  soDongTaoMoi: number
  chiTietLoi: ChiTietLoiNhapCapNhat[]
  danhSachHopLe: Array<{
    dong: number
    maHangHoa: string
    tenHangHoa: string
    hanhDong: 'CAP_NHAT' | 'TAO_MOI'
    soDonViQuyDoi?: number
  }>
}

export type BaoCaoDongCapNhat = {
  dong: number
  maHangHoa: string
  hanhDong: 'CAP_NHAT' | 'TAO_MOI' | 'LOI'
  truongThayDoi?: string[]
  giaTriCu?: Record<string, unknown>
  giaTriMoi?: Record<string, unknown>
  loi?: string
}

export type KetQuaNhapCapNhatHangHoa = {
  tongSoDong: number
  thanhCong: number
  thatBai: number
  baoCao: BaoCaoDongCapNhat[]
  chiTietLoi: ChiTietLoiNhapCapNhat[]
}

export type NhapCapNhatHangHoaPayload = NhapCapNhatHangHoaOptions & {
  danhSach: Record<string, unknown>[]
}

const buildNhapCapNhatBody = (
  danhSach: Record<string, unknown>[],
  opts?: NhapCapNhatHangHoaOptions,
): NhapCapNhatHangHoaPayload => ({
  danhSach,
  xuLyTrungMaKhacTen: opts?.xuLyTrungMaKhacTen ?? 'thayThe',
  xuLyTrungMaVachKhacMa: opts?.xuLyTrungMaVachKhacMa ?? 'baoLoi',
  phamViApDungGiaVon: opts?.phamViApDungGiaVon ?? 'toanHeThong',
  danhSachChiNhanhGiaVon: opts?.danhSachChiNhanhGiaVon ?? [],
  phamViApDungTrangThai: opts?.phamViApDungTrangThai ?? 'toanHeThong',
  danhSachChiNhanhTrangThai: opts?.danhSachChiNhanhTrangThai ?? [],
  capNhatTonKho: opts?.capNhatTonKho ?? false,
  capNhatMoTa: opts?.capNhatMoTa ?? false,
  capNhatGiaVon: opts?.capNhatGiaVon ?? false,
  capNhatDonViQuyDoi: opts?.capNhatDonViQuyDoi ?? false,
})

/** ~500+ dòng + ĐVT quy đổi dễ vượt timeout mặc định 60s. */
const NHAP_HANG_HOA_TIMEOUT_MS = 300_000

/** Bước 1: validate dry-run — KHÔNG ghi DB. Body JSON. */
export async function xacNhanNhapCapNhatHangHoa(
  danhSach: Record<string, unknown>[],
  opts?: NhapCapNhatHangHoaOptions,
): Promise<KetQuaXacNhanNhapCapNhat> {
  const raw = (await apiRequest(
    ApisServiceApp.post(
      `${PRODUCT_BASE}/xac-nhan-nhap-cap-nhat-hang-hoa-tu-excel`,
      buildNhapCapNhatBody(danhSach, opts),
      { timeout: NHAP_HANG_HOA_TIMEOUT_MS },
    ),
  )) as { data?: KetQuaXacNhanNhapCapNhat } & KetQuaXacNhanNhapCapNhat
  return (raw?.data ?? raw) as KetQuaXacNhanNhapCapNhat
}

/** Bước 2: apply — ghi DB (đã validate trước). Body JSON. */
export async function nhapCapNhatHangHoaTuExcel(
  danhSach: Record<string, unknown>[],
  opts?: NhapCapNhatHangHoaOptions,
): Promise<KetQuaNhapCapNhatHangHoa> {
  const raw = (await apiRequest(
    ApisServiceApp.post(
      `${PRODUCT_BASE}/nhap-cap-nhat-hang-hoa-tu-excel`,
      buildNhapCapNhatBody(danhSach, opts),
      { timeout: NHAP_HANG_HOA_TIMEOUT_MS },
    ),
  )) as { data?: KetQuaNhapCapNhatHangHoa } & KetQuaNhapCapNhatHangHoa
  return (raw?.data ?? raw) as KetQuaNhapCapNhatHangHoa
}

/** Tải file Excel danh sách dòng lỗi — build phía client. */
export async function taiFileLoiNhapCapNhat(
  chiTietLoi: ChiTietLoiNhapCapNhat[],
): Promise<Blob> {
  const { buildHangHoaLoiImportBlob } = await import(
    '@/components/admin/product/partials/hangHoaExcelImportParse'
  )
  return buildHangHoaLoiImportBlob(chiTietLoi)
}

export type ChiTietLoiNhapViTriHangHoa = {
  dong: number
  maHang?: string
  loi: string
}

export type KetQuaNhapViTriHangHoa = {
  thanhCong: number
  thatBai: number
  taoMoiViTri: number
  chiTietLoi: ChiTietLoiNhapViTriHangHoa[]
}

export type NhapViTriHangHoaOptions = {
  taoMoiViTri?: boolean
}

export type NhapViTriHangHoaDong = {
  rowNumber?: number
  maHangHoa: string
  maViTris: string[]
}

/** Import vị trí gắn hàng hóa — theo chi nhánh JWT đang đứng. */
export async function nhapViTriHangHoaTuExcel(
  danhSach: NhapViTriHangHoaDong[],
  opts?: NhapViTriHangHoaOptions,
): Promise<KetQuaNhapViTriHangHoa> {
  const raw = (await apiRequest(
    ApisServiceApp.post(`${PRODUCT_BASE}/nhap-vi-tri-hang-hoa-tu-excel`, {
      danhSach,
      taoMoiViTri: opts?.taoMoiViTri === true,
    }),
  )) as { data?: KetQuaNhapViTriHangHoa } & KetQuaNhapViTriHangHoa
  return (raw?.data ?? raw) as KetQuaNhapViTriHangHoa
}

/** Dòng import ảnh — cùng data shape mua hàng (Excel A=Mã SP, B=Ảnh). */
export type NhapAnhHangHoaDong = {
  rowNumber: number
  maSanPham: string
  hasImage?: boolean
  imageUrl?: string
}

export type ChiTietLoiNhapAnhHangHoa = {
  dong: number
  maHang?: string
  loi: string
}

export type KetQuaNhapAnhHangHoa = {
  thanhCong: number
  soSanPham: number
  thatBai: number
  chiTietLoi: ChiTietLoiNhapAnhHangHoa[]
}

const NHAP_ANH_TIMEOUT_MS = 300_000

/** Import ảnh hàng hóa — BE tự validate mã SP rồi gắn ảnh (giữ ảnh cũ). */
export async function nhapAnhHangHoa(
  danhSach: NhapAnhHangHoaDong[],
): Promise<KetQuaNhapAnhHangHoa> {
  const raw = (await apiRequest(
    ApisServiceApp.post(`${PRODUCT_BASE}/nhap-anh-hang-hoa`, danhSach, {
      timeout: NHAP_ANH_TIMEOUT_MS,
    }),
  )) as { data?: KetQuaNhapAnhHangHoa } & KetQuaNhapAnhHangHoa
  return (raw?.data ?? raw) as KetQuaNhapAnhHangHoa
}
